import { useCallback, useEffect, useRef, useState } from 'react';
import { FirebaseError } from 'firebase/app';
import { Speaker, LogOut, Mail, RefreshCw, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { activateBetaAccount, signUpErrorMessage } from '../../services/betaSignUp';

type PanelState = 'checking' | 'unverified' | 'activating' | 'not-activated' | 'error';

const NO_INVITE_MESSAGE = 'No invite on record';
const UNVERIFIED_MESSAGE = 'Verify your email first';

function isFailedPrecondition(error: unknown, messageStart: string): boolean {
  return error instanceof FirebaseError
    && error.code === 'functions/failed-precondition'
    && error.message.startsWith(messageStart);
}

export function AccountActivationPanel() {
  const { user, refreshClaims, resendVerificationEmail, logout } = useAuth();
  const [panelState, setPanelState] = useState<PanelState>('checking');
  const [errorMessage, setErrorMessage] = useState('');
  const [resending, setResending] = useState(false);
  const hasCheckedOnMount = useRef(false);

  const checkAndActivate = useCallback(async () => {
    if (!user) return;
    setPanelState('checking');
    try {
      await user.reload();
      if (!user.emailVerified) {
        setPanelState('unverified');
        return;
      }
      setPanelState('activating');
      await activateBetaAccount();
      const isDJ = await refreshClaims();
      if (!isDJ) {
        setErrorMessage('Your account was activated, but this page could not confirm it yet. Try again.');
        setPanelState('error');
      }
    } catch (error) {
      console.error('Account activation failed', error);
      if (isFailedPrecondition(error, NO_INVITE_MESSAGE)) {
        setPanelState('not-activated');
      } else if (isFailedPrecondition(error, UNVERIFIED_MESSAGE)) {
        setPanelState('unverified');
      } else {
        setErrorMessage(signUpErrorMessage(error));
        setPanelState('error');
      }
    }
  }, [user, refreshClaims]);

  useEffect(() => {
    if (hasCheckedOnMount.current) return;
    hasCheckedOnMount.current = true;
    void checkAndActivate();
  }, [checkAndActivate]);

  const handleResend = async () => {
    setResending(true);
    try {
      await resendVerificationEmail();
      toast.success('Verification email sent.');
    } catch (error) {
      console.error('Failed to resend verification email', error);
      toast.error('Could not send the email. Please try again in a few minutes.');
    } finally {
      setResending(false);
    }
  };

  const handleSignOut = () => {
    logout().catch((error) => console.error('Sign out failed', error));
  };

  const signOutButton = (className: string) => (
    <button type="button" className={className} onClick={handleSignOut}>
      <LogOut className="w-5 h-5 mr-2" />
      Sign out
    </button>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-light dark:bg-background-dark p-6">
      <div className="w-full max-w-md text-center space-y-4">
        <Speaker className="w-12 h-12 text-primary mx-auto" />

        {(panelState === 'checking' || panelState === 'activating') && (
          <>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {panelState === 'activating' ? 'Activating your account…' : 'Checking your account…'}
            </h1>
            <RefreshCw className="w-6 h-6 text-primary mx-auto animate-spin" />
          </>
        )}

        {panelState === 'unverified' && (
          <>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Check your email</h1>
            <p className="text-gray-600 dark:text-gray-400">
              We sent a verification link to <span className="font-medium text-gray-900 dark:text-white">{user?.email}</span>.
              Click it to activate your account.
            </p>
            <button type="button" className="btn btn-primary w-full" onClick={() => void checkAndActivate()}>
              <CheckCircle className="w-5 h-5 mr-2" />
              I've verified my email
            </button>
            <button type="button" className="btn btn-secondary w-full" onClick={handleResend} disabled={resending}>
              <Mail className="w-5 h-5 mr-2" />
              {resending ? 'Sending…' : 'Resend email'}
            </button>
            {signOutButton('btn btn-secondary w-full')}
          </>
        )}

        {panelState === 'not-activated' && (
          <>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Account not activated</h1>
            <p className="text-gray-600 dark:text-gray-400">
              This account isn't set up as a DJ account yet. Contact VoteBeats to get access.
            </p>
            {signOutButton('btn btn-primary w-full')}
          </>
        )}

        {panelState === 'error' && (
          <>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Something went wrong</h1>
            <p role="alert" className="text-gray-600 dark:text-gray-400">{errorMessage}</p>
            <button type="button" className="btn btn-primary w-full" onClick={() => void checkAndActivate()}>
              <RefreshCw className="w-5 h-5 mr-2" />
              Try again
            </button>
            {signOutButton('btn btn-secondary w-full')}
          </>
        )}
      </div>
    </div>
  );
}
